import axios, { type CreateAxiosDefaults } from 'axios';
import { env } from '@/utils/env';
import { refreshOnUnauthorized } from './interceptors/refresh.interceptor';
import { checkEnvelope, toApiError } from './interceptors/response.interceptor';
import { attachBusinessId } from './interceptors/tenant.interceptor';
import './request-config';

export const DEFAULT_TIMEOUT_MS = 15_000;

// Builds the axios instance every request goes through: <VITE_API_URL>/api/v1, session cookies
// attached. `overrides` exists for tests (e.g. a fake adapter).
export function createApiClient(overrides: CreateAxiosDefaults = {}) {
  const client = axios.create({
    baseURL: env.apiBaseUrl,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT_MS,
    headers: { Accept: 'application/json' },
    // Report timeouts as ETIMEDOUT instead of the generic ECONNABORTED.
    transitional: { clarifyTimeoutError: true },
    ...overrides,
  });

  client.interceptors.request.use(attachBusinessId);
  // Order matters: the refresh step needs the raw AxiosError (with its request config), so it
  // runs before errors are converted to ApiError.
  client.interceptors.response.use(undefined, (error: unknown) =>
    refreshOnUnauthorized(client, error),
  );
  client.interceptors.response.use(checkEnvelope, toApiError);

  return client;
}

export const apiClient = createApiClient();
